import { CheckCircle, HelpOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useMemo } from 'react';

import { formatPhoneDisplay } from 'features/customers/helpers/formatting';

import {
  candidateLabel,
  MERGE_FIELDS,
  type MergeFieldKey,
  type WizardCandidate,
} from './merge-wizard-types';

export interface MergeWizardCompareStepProps {
  candidates: WizardCandidate[];
  masterId: string;
  onMasterChange: (id: string) => void;
  fieldSources: Record<MergeFieldKey, string>;
  onFieldSourceChange: (field: MergeFieldKey, sourceId: string) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
}

const rawValue = (c: WizardCandidate, key: MergeFieldKey): string =>
  (c[key] ?? '').toString().trim();

// Display formatting (phone → (555) 123-4567); diffing stays on raw values.
const displayValue = (c: WizardCandidate, key: MergeFieldKey): string => {
  const value = rawValue(c, key);
  if (!value) return '—';
  return key === 'phone' ? formatPhoneDisplay(value) : value;
};

function fieldDiffers(
  candidates: WizardCandidate[],
  key: MergeFieldKey,
): boolean {
  const values = candidates.map((c) => rawValue(c, key));
  return new Set(values).size > 1;
}

// A whole cell is clickable to select that record's value for the row.
const selectableCellSx =
  (selected: boolean, isMasterColumn: boolean, disabled: boolean) =>
  (theme: Theme) => {
    if (disabled) {
      return {
        cursor: 'not-allowed',
        userSelect: 'none' as const,
        opacity: 0.45,
        bgcolor: 'transparent',
        borderLeft: '3px solid transparent',
      };
    }
    let bg = 'transparent';
    if (selected) bg = alpha(theme.palette.primary.main, 0.12);
    else if (isMasterColumn) bg = alpha(theme.palette.primary.main, 0.04);
    return {
      cursor: 'pointer',
      userSelect: 'none' as const,
      bgcolor: bg,
      borderLeft: `3px solid ${
        selected ? theme.palette.primary.main : 'transparent'
      }`,
      transition: 'background-color .12s ease',
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, selected ? 0.16 : 0.08),
      },
    };
  };

export function MergeWizardCompareStep({
  candidates,
  masterId,
  onMasterChange,
  fieldSources,
  onFieldSourceChange,
  showAll,
  onToggleShowAll,
}: MergeWizardCompareStepProps) {
  const visibleFields = useMemo(
    () =>
      MERGE_FIELDS.filter((f) => showAll || fieldDiffers(candidates, f.key)),
    [candidates, showAll],
  );

  // If any record has a login, only a login-bearing record can be the primary —
  // the survivor keeps its own Cognito account, so it has to have one. Any
  // field may be taken from any record, including a login-holder's email/phone:
  // the merge releases the retired account's alias before the survivor claims
  // it (see applyMergeCognito).
  const anyHasLogin = candidates.some((c) => Boolean(c.hasLogin));

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        When you merge, the primary record is updated with the values you
        choose, and relationships to other items are shifted to the primary
        record.
      </Typography>

      <Table
        size="small"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& td, & th': { borderBottom: 1, borderColor: 'divider' },
          '& td': { p: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 160 }} />
            {candidates.map((c) => (
              <TableCell
                key={c._id}
                sx={{ py: 1.5, width: `${100 / candidates.length}%` }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {candidateLabel(c)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                  Primary record
                </Typography>
                <Tooltip title="The record that is kept. The others are retired into it.">
                  <HelpOutline sx={{ fontSize: 16, color: 'action.active' }} />
                </Tooltip>
              </Stack>
            </TableCell>
            {candidates.map((c) => {
              const selected = masterId === c._id;
              const disabled = anyHasLogin && !c.hasLogin;
              const cell = (
                <TableCell
                  key={c._id}
                  onClick={disabled ? undefined : () => onMasterChange(c._id)}
                  sx={selectableCellSx(selected, selected, disabled)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    {selected && (
                      <CheckCircle sx={{ fontSize: 18 }} color="primary" />
                    )}
                    <Typography
                      variant="body2"
                      color={selected ? 'primary.main' : 'text.primary'}
                      fontWeight={selected ? 600 : 400}
                    >
                      Use as primary
                    </Typography>
                  </Box>
                </TableCell>
              );
              return disabled ? (
                <Tooltip
                  key={c._id}
                  title="Only a customer with a login can be the primary."
                >
                  {cell}
                </Tooltip>
              ) : (
                cell
              );
            })}
          </TableRow>

          {visibleFields.map((field) => (
            <TableRow key={field.key}>
              <TableCell sx={{ pl: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  {field.label}
                </Typography>
              </TableCell>
              {candidates.map((c) => {
                const selected = fieldSources[field.key] === c._id;
                const isMasterColumn = masterId === c._id;
                return (
                  <TableCell
                    key={c._id}
                    onClick={() => onFieldSourceChange(field.key, c._id)}
                    sx={selectableCellSx(selected, isMasterColumn, false)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1.25,
                        minHeight: 24,
                      }}
                    >
                      {selected && (
                        <CheckCircle sx={{ fontSize: 18 }} color="primary" />
                      )}
                      <Typography
                        variant="body2"
                        color={selected ? 'primary.main' : 'text.primary'}
                        fontWeight={selected ? 600 : 400}
                      >
                        {displayValue(c, field.key)}
                      </Typography>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" component="span">
          {showAll
            ? 'Showing all fields. '
            : 'Showing fields with different values. '}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={onToggleShowAll}
          sx={{
            textTransform: 'none',
            verticalAlign: 'baseline',
            p: 0,
            minWidth: 0,
          }}
        >
          {showAll ? 'Show only differences' : 'Show all fields'}
        </Button>
      </Box>
    </Stack>
  );
}
